import AppButton from '@nxsys/components/AppButton';
import AppSwitch from '@nxsys/components/AppSwitch';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import { taxCodeValidationRule } from '@nxsys/utils/formFieldValidation';
import AppLoader from '@nxsys/components/AppLoader';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import {
  defaultASCCalculationMethodOptions,
  defaultCisStatus,
  defaultPartnerCategoryOptions,
  defaultPaymentFrequency,
  defaultPRSIEeClassesOptions,
  defaultPRSIExemptionOptions,
  defaultsBusinessTypeOptions,
  defaultTaxBasisIrelandOptions,
  defaultTaxBasisOptions,
  defaultTaxExclusionOptions,
  defaultUSCExemptionOptions,
  defaultUSCTiersOptions,
  directorshipOptions,
  employmentStatusOptions,
  holidayPaymentMethodOptions,
  getHolidayPaymentMethodOptions,
  freeportNIcategories,
  NICategoryOptions,
  PAYMENT_MODEL_ENUM,
  getIrelandPaymentModelOptions,
  rctStatusOptions,
  starterDeclarationOptions,
  starterTypeOptions,
  USCRateList,
} from '@nxsys/constants/AppConstants';
import {
  BusinessTypeEnum,
  enumMarginMethod,
  YesNoNAEnum,
} from '@nxsys/constants/AppEnums';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  useGetEmployeeById,
  useUpdateSettingEmployeeById,
} from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { UpdateSettingEmployeeByIdParams } from '@nxsys/types/api';
import { Icons } from '@nxsys/types/icons';
import {
  calcUSCRateAmount,
  calcUSCRateFrequency,
  defaultMarginMethod,
  defaultPaymentTerm,
  getAppConfig,
  isValidPPSNumber,
  showCurrencyByCountry,
  isNIDirectorMidYear,
} from '@nxsys/utils';
import { getFieldLabelByCountry } from '@nxsys/utils/labelConfig';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import Inheritable from '@nxsys/components/Inheritable';
import { Col, Collapse, Form, Row, Switch, Typography } from 'antd';
import { toNumber } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import MarginMethodField from './components/MarginMethodField';
import { isTableMarginMethod } from '@nxsys/utils/margin';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppPercentageInput from '@nxsys/components/AppPercentageInput';
import { useDrawerSave } from './useDrawerSave';

type SettingsGeneralProps = {
  readOnly?: boolean;
  /** Worker id when embedded outside its own route (NX-4682 quick-edit). */
  employeeId?: string;
  /** Field name to scroll to + focus once data loads (NX-4682 quick-edit). */
  focusField?: string;
};

const NIDirectorMethodOptions = [
  { label: 'Annual', value: 'ANNUAL' },
  { label: 'Alternative', value: 'ALTERNATIVE' },
];

const SettingsGeneral = ({
  readOnly,
  employeeId: employeeIdProp,
  focusField,
}: SettingsGeneralProps = {}) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm();
  const watchValues = Form.useWatch([], form);

  // NX-6426: the six "(as director)" opening balances are zeroed by the backend
  // unless the director was appointed part-way through the current tax year
  // (subcontractor.setting.service.ts:377). Gate the inputs on the same rule,
  // computed from the date currently in the form so it reacts as the user edits.
  const isNIMiddleJoined = isNIDirectorMidYear(
    watchValues?.isNIDirector,
    watchValues?.NIDirectorStartDate,
  );
  // A table-based margin method takes the amount from the band table, so the
  // flat Margin field must not be editable.
  const isMarginFromTable = isTableMarginMethod(
    watchValues?.marginMethod?.value,
  );
  const previousPayrollIdUnknown = Form.useWatch(
    'previousPayrollIdUnknown',
    form,
  );
  const previousPayrollNo = Form.useWatch('previousPayrollNo', form);
  const isPayeModel =
    watchValues?.paymentModel?.value === PAYMENT_MODEL_ENUM.PAYEE;
  const isRctModel =
    watchValues?.paymentModel?.value === PAYMENT_MODEL_ENUM.CIS;
  const [showOpeningBalance, setShowOpeningBalance] = useState(false);
  const params = useParams();
  const { payrollCountry, isBureau, isEmployee, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const dispatch = useDispatch<AppDispatch>();
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  const isIreland = payrollCountry === IRELAND;
  // Country-only, unlike `isUK` above: replaces the `!isBureau || isUK` OR
  // gate that used to stand in for "any umbrella tenant, or bureau-UK". That
  // OR is algebraically `!isBureau || payrollCountry===UK`, which was `true`
  // for umbrella-Ireland too (a bug — UK payment-rule fields leaking into an
  // Irish umbrella employee record). `isUKCountry` alone fixes only that
  // unshipped cell; the three shipped cells are unchanged.
  const isUKCountry = payrollCountry === UNITED_KINGDOM;
  const employeeId = isEmployee
    ? user?.subcontractorId?.id
    : (employeeIdProp ?? params?.employeeId ?? null);

  const isCisNonCis =
    watchValues?.paymentModel?.value === PAYMENT_MODEL_ENUM.CIS ||
    watchValues?.paymentModel?.value === PAYMENT_MODEL_ENUM.NON_CIS;

  const defaultPaymentModel = isIreland
    ? getIrelandPaymentModelOptions(isBureau)
    : getAppConfig('paymentModels');

  const {
    run: fetchEmployee,
    loading: isFetchingEmployee,
    value: employeeValue,
  } = useGetEmployeeById();
  const employeeData = useMemo(() => employeeValue?.data, [employeeValue]);

  const frequencyLabel =
    defaultPaymentFrequency.find(
      (option) => option.value === employeeData?.payFrequency,
    )?.label || '-';
  const isFetchingPayrollSetting = useSelector(
    (state: AppState) => state.employeeStore.employeeSetting.loading,
  );
  const payrollSettingData = useSelector(
    (state: AppState) => state.employeeStore.employeeSetting.data,
  );

  // Scroll to + focus a field when opened from the dashboard "Action Required"
  // quick-edit drawer (NX-4682). No-op on the standalone detail page.
  useEffect(() => {
    if (!focusField || !payrollSettingData) return;
    const timer = setTimeout(() => {
      // AppInput doesn't forward focus() to its native input, and antd prefixes
      // field ids with the form name — target the control via its id suffix.
      const el = document.querySelector<HTMLElement>(
        `[id="${focusField}"], [id$="_${focusField}"]`,
      );
      if (el) {
        // Smoothly center the whole field, then focus WITHOUT re-scrolling so
        // the browser's focus auto-scroll doesn't clobber the animation.
        const target = el.closest('.ant-form-item') ?? el;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      } else {
        form.scrollToField(focusField, { behavior: 'smooth', block: 'center' });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [focusField, payrollSettingData, form]);
  const fetchPayrollSetting = useCallback(
    (id: string) => dispatch(employeeActions.getPayrollSettingByEmployeeId(id)),
    [dispatch],
  );
  const getEmployeeById = useCallback(
    (id: string) => dispatch(employeeActions.getEmployeeById(id)),
    [dispatch],
  );
  const { run: updateSettingEmployeeById, loading: isUpdatingSettingEmployee } =
    useUpdateSettingEmployeeById();

  useEffect(() => {
    if (employeeId) {
      fetchPayrollSetting(employeeId);
      fetchEmployee(employeeId);
    }
  }, [employeeId]);

  // Populate form when data loads
  useEffect(() => {
    if (!payrollSettingData) return;
    form.setFieldsValue({
      // Payment Rule fields
      paymentModel:
        (defaultPaymentModel ?? []).find(
          (o) => o.value === payrollSettingData.paymentModel,
        ) ?? null,
      businessType:
        defaultsBusinessTypeOptions.find(
          (o) => o.value === payrollSettingData.businessType,
        ) ?? null,
      primaryAgency: payrollSettingData.primaryAgency
        ? {
            label: payrollSettingData.primaryAgency.name,
            value: payrollSettingData.primaryAgency.id,
          }
        : null,
      secondaryAgency: payrollSettingData.secondaryAgency
        ? {
            label: payrollSettingData.secondaryAgency.name,
            value: payrollSettingData.secondaryAgency.id,
          }
        : null,
      paymentTerm:
        defaultPaymentTerm.find(
          (o) => o.value === payrollSettingData.paymentTerm,
        ) ?? null,
      marginMethod:
        defaultMarginMethod.find(
          (o) => o.value === payrollSettingData.marginMethod,
        ) ?? null,
      margin: payrollSettingData.margin ?? '',
      marginCapped: payrollSettingData.marginCapped ?? '',
      minMargin: payrollSettingData.minMargin ?? '',
      autoReduceMarginForNMW: payrollSettingData.autoReduceMarginForNMW ?? null,
      assignmentRequired: payrollSettingData.assignmentRequired ?? false,
      timesheetApprovalNeeded:
        payrollSettingData.timesheetApprovalNeeded === YesNoNAEnum.YES
          ? true
          : payrollSettingData.timesheetApprovalNeeded === YesNoNAEnum.NO
            ? false
            : null,
      timesheetApprover: payrollSettingData?.timesheetApprover
        ? payrollSettingData.timesheetApprover.map((u: any) => ({
            label: `${u?.forename || ''} ${u?.surname || ''}`.trim() || u?.name,
            value: u.id,
          }))
        : [],
      listEmprApprovers: payrollSettingData?.listEmprApprovers
        ? payrollSettingData.listEmprApprovers.map((u: any) => ({
            label: `${u?.forename || ''} ${u?.surname || ''}`.trim() || u?.name,
            value: u.id,
          }))
        : [],
      holidayPayMethod:
        holidayPaymentMethodOptions.find(
          (o) => o.value === payrollSettingData.holidayPayMethod,
        ) ?? null,
      enableAWRTracking: payrollSettingData.enableAWRTracking ?? false,
      enableWTR: payrollSettingData.enableWTR ?? null,
      notificationWeeks: payrollSettingData.notificationWeeks ?? '',
      offPayrollWorker: payrollSettingData.offPayrollWorker ?? false,
      irregularPaidWorker: payrollSettingData.irregularPaidWorker ?? false,
      enableBenefits: payrollSettingData.enableBenefits ?? null,
      // NX-5013: outsideUKWorker is stored on subcontractor.pensionSetting
      // (sub.pension entity), not on the Setting entity returned by
      // /subcontractor/setting/:id. Reading payrollSettingData.outsideUKWorker
      // is always undefined, the form defaults to false, then submit ships
      // false and the BE coerces to false on the JSON column — wiping whatever
      // the user had set via the Others tab. Loading from
      // employeeData.pensionSetting.outsideUKWorker so the value round-trips.
      outsideUKWorker: employeeData?.pensionSetting?.outsideUKWorker ?? false,
      payrollApproveNeeded:
        payrollSettingData.payrollApproveNeeded === YesNoNAEnum.YES
          ? true
          : payrollSettingData.payrollApproveNeeded === YesNoNAEnum.NO
            ? false
            : null,
      includeInTimesheetRate:
        payrollSettingData.includeInTimesheetRate ?? false,
      holidayPayRate: payrollSettingData.holidayPayRate ?? '',
      isNIDirector: payrollSettingData.isNIDirector ?? false,
      NIDirectorStartDate: payrollSettingData.NIDirectorStartDate ?? null,
      NIDirectorMethod:
        NIDirectorMethodOptions.find(
          (o) => o.value === payrollSettingData.NIDirectorMethod,
        ) ?? null,
      directorShip:
        directorshipOptions.find(
          (o) => o.value === payrollSettingData.directorShip,
        ) ?? null,
      restrictedHours: (payrollSettingData as any)?.restrictedHours ?? null,
      // CIS/Non-CIS Tax Settings fields
      cisStatus: isIreland
        ? (rctStatusOptions.find(
            (o) => o.value === payrollSettingData.cisStatus,
          ) ?? null)
        : (defaultCisStatus.find(
            (o) => o.value === payrollSettingData.cisStatus,
          ) ?? null),
      subcontractorRefNumber: payrollSettingData.subcontractorRefNumber ?? '',
      personalVerificationNumber:
        payrollSettingData.personalVerificationNumber ?? '',
      matchDate: payrollSettingData.matchDate ?? null,
      verificationDate: payrollSettingData.verificationDate ?? null,
      UTRNo: payrollSettingData.UTRNo ?? '',
      VATRegNo: payrollSettingData.VATRegNo ?? '',
      domesticReverseChargeApplicable:
        payrollSettingData.domesticReverseChargeApplicable ?? null,
      companyName: payrollSettingData.companyName ?? '',
      registrationNumber: payrollSettingData.registrationNumber ?? '',
      firmName: payrollSettingData.firmName ?? '',
      partnersName: (payrollSettingData as any)?.partnersName ?? '',
      partnersCategory:
        defaultPartnerCategoryOptions.find(
          (o) => o.value === payrollSettingData.partnersCategory,
        ) ?? null,
      // Tax / NI / Starter Declaration fields
      taxCode: payrollSettingData.taxCode ?? '',
      taxBasis:
        defaultTaxBasisOptions.find(
          (o) => o.value === payrollSettingData.taxBasis,
        ) ?? null,
      niCategory:
        NICategoryOptions.find(
          (o) => o.value === payrollSettingData.niCategory,
        ) ?? null,
      starterType:
        starterTypeOptions.find(
          (o) => o.value === payrollSettingData.starterType,
        ) ?? null,
      starterDeclaration:
        starterDeclarationOptions.find(
          (o) => o.value === payrollSettingData.starterDeclaration,
        ) ?? null,
      p45GrossToDate: payrollSettingData.p45GrossToDate ?? '',
      p45TaxToDate: payrollSettingData.p45TaxToDate ?? '',
      previousPayrollNo: payrollSettingData.previousPayrollNo ?? '',
      previousPayrollIdUnknown:
        payrollSettingData.previousPayrollIdUnknown ?? false,
      // P11 / Opening Balance fields
      employeeNI: payrollSettingData.p11?.eeNI ?? '',
      employerNI: payrollSettingData.p11?.erNI ?? '',
      employeePension: payrollSettingData.p11?.eePension ?? '',
      employerPension: payrollSettingData.p11?.erPension ?? '',
      studentLoan: payrollSettingData.p11?.stLoan ?? '',
      postGraduateStudentLoan: payrollSettingData.p11?.pgLoan ?? '',
      smp: payrollSettingData.p11?.smp ?? '',
      spp: payrollSettingData.p11?.spp ?? '',
      sap: payrollSettingData.p11?.sap ?? '',
      shpp: payrollSettingData.p11?.shpp ?? '',
      ssp: payrollSettingData.p11?.ssp ?? '',
      sncp: payrollSettingData.p11?.sncp ?? '',
      grossToLel: payrollSettingData.p11?.grossToLel ?? '',
      grossLelToPt: payrollSettingData.p11?.grossLelToPt ?? '',
      grossPtToUEL: payrollSettingData.p11?.grossPtToUEL ?? '',
      gross: payrollSettingData.p11?.gross ?? '',
      grossForTax: payrollSettingData.p11?.grossForTax ?? '',
      grossForNI: payrollSettingData.p11?.grossForNI ?? '',
      tax: payrollSettingData.p11?.tax ?? '',
      // Director NI Contribution fields
      grossToLelAsDirector: payrollSettingData.p11?.grossToLelAsDirector ?? '',
      grossLelToPtAsDirector:
        payrollSettingData.p11?.grossLelToPtAsDirector ?? '',
      grossPtToUELAsDirector:
        payrollSettingData.p11?.grossPtToUELAsDirector ?? '',
      grossForNIAsDirector: payrollSettingData.p11?.grossForNIAsDirector ?? '',
      eeNIAsDirector: payrollSettingData.p11?.eeNIAsDirector ?? '',
      erNIAsDirector: payrollSettingData.p11?.erNIAsDirector ?? '',
      // Ireland Opening Balance (P11) fields
      eePRSI: (payrollSettingData.p11 as any)?.eePRSI ?? '',
      erPRSI: (payrollSettingData.p11 as any)?.erPRSI ?? '',
      eePrsiGross: (payrollSettingData.p11 as any)?.eePrsiGross ?? '',
      erPrsiGross: (payrollSettingData.p11 as any)?.erPrsiGross ?? '',
      uscGross: (payrollSettingData.p11 as any)?.uscGross ?? '',
      usc: (payrollSettingData.p11 as any)?.usc ?? '',
      pensionGross: (payrollSettingData.p11 as any)?.pensionGross ?? '',
      lptDeduction: (payrollSettingData.p11 as any)?.lptDeduction ?? '',
      // Ireland-specific fields
      employmentStatus:
        employmentStatusOptions.find(
          (o) => o.value === payrollSettingData.employmentStatus,
        ) ?? null,
      ppsNumber: payrollSettingData.ppsNumber ?? '',
      employmentId: payrollSettingData.employmentId ?? '',
      taxExclusion:
        defaultTaxExclusionOptions.find(
          (o) => o.value === payrollSettingData.taxExclusion,
        ) ?? null,
      taxCreditAnnual: payrollSettingData.taxCreditAnnual?.toString() ?? '',
      taxCreditFrequency: payrollSettingData.taxCreditAnnual
        ? calcUSCRateFrequency(
            employeeData?.payFrequency || 'WEEKLY',
            payrollSettingData.taxCreditAnnual || 0,
          )?.toString() || ''
        : '',
      taxRateCutOffPoints:
        payrollSettingData.taxRateCutOffPoints?.[0]?.yearlyRateCutOff || null,
      taxRateCutOffPointsFrequency: payrollSettingData.taxRateCutOffPoints?.[0]
        ?.yearlyRateCutOff
        ? calcUSCRateFrequency(
            employeeData?.payFrequency || 'WEEKLY',
            payrollSettingData.taxRateCutOffPoints?.[0]?.yearlyRateCutOff || 0,
          )?.toString() || ''
        : '',
      uscExemption:
        defaultUSCExemptionOptions.find(
          (o) => o.value === payrollSettingData.uscExemption,
        ) ?? null,
      cutOffPointsType:
        defaultUSCTiersOptions.find(
          (o) => o.value === (payrollSettingData as any).cutOffPoints?.type,
        ) ?? null,
      cutOffPointsZeroPointFive:
        (payrollSettingData as any).cutOffPoints?.values?.[0]
          ?.yearlyUSCRateCutOff || '',
      cutOffPointsZeroPointFiveFrequency: (payrollSettingData as any)
        .cutOffPoints?.values?.[0]?.yearlyUSCRateCutOff
        ? calcUSCRateFrequency(
            employeeData?.payFrequency || 'WEEKLY',
            (payrollSettingData as any).cutOffPoints?.values?.[0]
              ?.yearlyUSCRateCutOff || 0,
          )?.toString() || ''
        : '',
      cutOffPointsTwo:
        (payrollSettingData as any).cutOffPoints?.values?.[1]
          ?.yearlyUSCRateCutOff || '',
      cutOffPointsTwoFrequency: (payrollSettingData as any).cutOffPoints
        ?.values?.[1]?.yearlyUSCRateCutOff
        ? calcUSCRateFrequency(
            employeeData?.payFrequency || 'WEEKLY',
            (payrollSettingData as any).cutOffPoints?.values?.[1]
              ?.yearlyUSCRateCutOff || 0,
          )?.toString() || ''
        : '',
      cutOffPointsFourPointFive:
        (payrollSettingData as any).cutOffPoints?.values?.[2]
          ?.yearlyUSCRateCutOff || '',
      cutOffPointsFourPointFiveFrequency: (payrollSettingData as any)
        .cutOffPoints?.values?.[2]?.yearlyUSCRateCutOff
        ? calcUSCRateFrequency(
            employeeData?.payFrequency || 'WEEKLY',
            (payrollSettingData as any).cutOffPoints?.values?.[2]
              ?.yearlyUSCRateCutOff || 0,
          )?.toString() || ''
        : '',
      prsiEeClasses:
        defaultPRSIEeClassesOptions.find(
          (o) => o.value === payrollSettingData.prsiEeClasses,
        ) ?? null,
      prsiExemption:
        defaultPRSIExemptionOptions.find(
          (o) => o.value === payrollSettingData.prsiExemption,
        ) ?? null,
      totalLPTToBeDeducted:
        payrollSettingData.totalLPTToBeDeducted?.toString() ?? '',
      ascCalculationMethod:
        defaultASCCalculationMethodOptions.find(
          (o) => o.value === payrollSettingData.ascCalculationMethod,
        ) ?? null,
      niNumber: employeeData?.NINumber || '',
    });
  }, [payrollSettingData, employeeData]);

  const handleSave = async () => {
    if (!employeeId) return;
    try {
      await form.validateFields();
    } catch {
      return;
    }
    // NX-5013: getFieldsValue(true) returns ALL form values, including those
    // populated via setFieldsValue for Form.Items that haven't mounted (e.g.
    // the collapsed Opening Balance section). Default getFieldsValue() returns
    // only currently-mounted fields, so unmounted fields would be undefined and
    // the `|| null` coercion below would null-wipe the persisted value on the BE.
    const v = form.getFieldsValue(true);

    let submitParams: UpdateSettingEmployeeByIdParams;

    // NX-5228: country decides the payload shape, not product — `isIreland`
    // must win over `!isBureau` here, else an umbrella-Ireland tenant always
    // falls into the UK-umbrella branch below and every Irish field
    // (PPSN/PRSI/USC/tax basis) is silently dropped on save. This is the same
    // class of bug that lost margin values in the pre-revert demo.
    if (isIreland) {
      submitParams = {
        paymentModel: v.paymentModel?.value || null,
        cisStatus: v.cisStatus?.value || null,
        subcontractorRefNumber: v.subcontractorRefNumber || null,
        assignmentRequired: v.assignmentRequired ?? false,
        timesheetApprovalNeeded:
          v.timesheetApprovalNeeded === true
            ? YesNoNAEnum.YES
            : v.timesheetApprovalNeeded === false
              ? YesNoNAEnum.NO
              : YesNoNAEnum.NOT_AVAILABLE,
        listEmprApprovers:
          v.timesheetApprovalNeeded === true
            ? (v.listEmprApprovers ?? []).map(
                (item: any) => item.value ?? item.id,
              )
            : null,
        payrollApproveNeeded:
          v.payrollApproveNeeded === true
            ? YesNoNAEnum.YES
            : v.payrollApproveNeeded === false
              ? YesNoNAEnum.NO
              : YesNoNAEnum.NOT_AVAILABLE,
        directorShip: v.directorShip?.value || null,
        holidayPayMethod: v.holidayPayMethod?.value || null,
        holidayPayRate: v.holidayPayRate || null,
        employmentStatus: v.employmentStatus?.value || null,
        ppsNumber: v.ppsNumber || null,
        employmentId: v.employmentId || null,
        taxBasis: v.taxBasis?.value || null,
        taxExclusion: v.taxExclusion?.value || null,
        taxCreditAnnual: v.taxCreditAnnual || null,
        taxRateCutOffPoints: v.taxRateCutOffPoints
          ? [
              {
                index: 1,
                taxRatePercent: '',
                yearlyRateCutOff: v.taxRateCutOffPoints,
              },
            ]
          : null,
        uscExemption: v.uscExemption?.value || null,
        cutOffPoints: v.cutOffPointsType
          ? {
              type: v.cutOffPointsType?.value || null,
              values: [
                {
                  index: 1,
                  uscRatePercent: 0.5,
                  yearlyUSCRateCutOff: v.cutOffPointsZeroPointFive || null,
                },
                {
                  index: 2,
                  uscRatePercent: 2,
                  yearlyUSCRateCutOff: v.cutOffPointsTwo || null,
                },
                {
                  index: 3,
                  uscRatePercent: 3,
                  yearlyUSCRateCutOff: v.cutOffPointsFourPointFive || null,
                },
                {
                  index: 4,
                  uscRatePercent: 8,
                },
              ],
            }
          : null,
        prsiEeClasses: v.prsiEeClasses?.value || null,
        prsiExemption: v.prsiExemption?.value || null,
        totalLPTToBeDeducted: v.totalLPTToBeDeducted || null,
        ascCalculationMethod: v.ascCalculationMethod?.value || null,
        // Opening Balance (P11) — Ireland
        p11: {
          eePRSI: Number(v.eePRSI) || 0,
          erPRSI: Number(v.erPRSI) || 0,
          eePension: Number(v.employeePension) || 0,
          erPension: Number(v.employerPension) || 0,
          eePrsiGross: Number(v.eePrsiGross) || 0,
          erPrsiGross: Number(v.erPrsiGross) || 0,
          uscGross: Number(v.uscGross) || 0,
          usc: Number(v.usc) || 0,
          pensionGross: Number(v.pensionGross) || 0,
          lptDeduction: Number(v.lptDeduction) || 0,
          gross: Number(v.gross) || 0,
          grossForTax: Number(v.grossForTax) || 0,
          tax: Number(v.tax) || 0,
          ssp: Number(v.ssp) || 0,
        },
        // Umbrella-only: the Ireland UMBRELLA payment model still needs
        // margin/agency, which live in the isIreland-gated JSX block above
        // (re-homed there, not in the UK-umbrella block below). Bureau-Ireland
        // has no such fields in its form, so this stays empty for that cell.
        ...(!isBureau
          ? {
              primaryAgency: v.primaryAgency?.value ?? null,
              secondaryAgency: v.secondaryAgency?.value ?? null,
              paymentTerm: v.paymentTerm?.value || null,
              marginMethod: v.marginMethod?.value || null,
              margin: v.margin || null,
              marginCapped: v.marginCapped || null,
              minMargin: v.minMargin || null,
              // No autoReduceMarginForNMW: margin auto-reduction is UK-only
              // and the field is not rendered for Ireland, so sending it would
              // only reset the stored value.
            }
          : {}),
      } as any;
    } else if (!isBureau) {
      submitParams = {
        paymentModel: v.paymentModel?.value || null,
        businessType: v.businessType?.value || null,
        primaryAgency: v.primaryAgency?.value ?? null,
        secondaryAgency: v.secondaryAgency?.value ?? null,
        paymentTerm: v.paymentTerm?.value || null,
        marginMethod: v.marginMethod?.value || null,
        margin: v.margin || null,
        marginCapped: v.marginCapped || null,
        minMargin: v.minMargin || null,
        // NX-5555: tristate — explicit false must survive (never `|| null`)
        autoReduceMarginForNMW: v.autoReduceMarginForNMW ?? null,
        assignmentRequired: v.assignmentRequired ?? false,
        timesheetApprovalNeeded:
          v.timesheetApprovalNeeded === true
            ? YesNoNAEnum.YES
            : v.timesheetApprovalNeeded === false
              ? YesNoNAEnum.NO
              : YesNoNAEnum.NOT_AVAILABLE,
        timesheetApprover:
          v.timesheetApprovalNeeded === true
            ? (v.timesheetApprover ?? []).map(
                (item: any) => item.value ?? item.id,
              )
            : null,
        holidayPayMethod: v.holidayPayMethod?.value || null,
        enableAWRTracking: v.enableAWRTracking ?? null,
        enableWTR: v.enableWTR ?? null,
        notificationWeeks: v.notificationWeeks || null,
        offPayrollWorker: v.offPayrollWorker ?? null,
        irregularPaidWorker: v.irregularPaidWorker ?? null,
        enableBenefits: v.enableBenefits ?? null,
        outsideUKWorker: v.outsideUKWorker ?? null,
        // CIS/Non-CIS Tax Settings
        cisStatus: v.cisStatus?.value || null,
        personalVerificationNumber: v.personalVerificationNumber || null,
        VATRegNo: v.VATRegNo || null,
        domesticReverseChargeApplicable:
          v.domesticReverseChargeApplicable ?? null,
        companyName: v.companyName ?? '',
        registrationNumber: v.registrationNumber || null,
        firmName: v.firmName ?? '',
        matchDate: v.matchDate || null,
        verificationDate: v.verificationDate || null,
        UTRNo: v.UTRNo || null,
        partnersCategory: v.partnersCategory?.value || null,
        // Tax / NI / Starter Declaration (for non-CIS)
        taxCode: v.taxCode ?? '',
        niCategory: v.niCategory?.value || null,
        starterDeclaration: v.starterDeclaration?.value || null,
        p45TaxToDate: v.p45TaxToDate || null,
        taxBasis: v.taxBasis?.value || null,
        starterType: v.starterType?.value || null,
        previousPayrollNo: v.previousPayrollNo || null,
        previousPayrollIdUnknown: v.previousPayrollIdUnknown ?? false,
        p45GrossToDate: v.p45GrossToDate || null,
        // Opening Balance (P11)
        p11: {
          eeNI: v.employeeNI || null,
          erNI: v.employerNI || null,
          eePension: v.employeePension || null,
          erPension: v.employerPension || null,
          stLoan: v.studentLoan || null,
          pgLoan: v.postGraduateStudentLoan || null,
          ssp: v.ssp || null,
          smp: v.smp || null,
          sap: v.sap || null,
          spp: v.spp || null,
          sppa: v.sppa || null,
          shpp: v.shpp || null,
          sncp: v.sncp || null,
          grossToLel: v.grossToLel || null,
          grossLelToPt: v.grossLelToPt || null,
          grossPtToUEL: v.grossPtToUEL || null,
          gross: v.gross || null,
          grossForTax: v.grossForTax || null,
          grossForNI: v.grossForNI || null,
          tax: v.tax || null,
          // Director NI Contribution
          grossToLelAsDirector: v.grossToLelAsDirector || null,
          grossLelToPtAsDirector: v.grossLelToPtAsDirector || null,
          grossPtToUELAsDirector: v.grossPtToUELAsDirector || null,
          grossForNIAsDirector: v.grossForNIAsDirector || null,
          eeNIAsDirector: v.eeNIAsDirector || null,
          erNIAsDirector: v.erNIAsDirector || null,
        } as any,
      };
    } else {
      // UK Bureau (PAYEE)
      submitParams = {
        paymentModel: PAYMENT_MODEL_ENUM.PAYEE,
        businessType: v.businessType?.value || null,
        assignmentRequired: v.assignmentRequired ?? false,
        timesheetApprovalNeeded:
          v.timesheetApprovalNeeded === true
            ? YesNoNAEnum.YES
            : v.timesheetApprovalNeeded === false
              ? YesNoNAEnum.NO
              : YesNoNAEnum.NOT_AVAILABLE,
        listEmprApprovers:
          v.timesheetApprovalNeeded === true
            ? (v.listEmprApprovers ?? []).map(
                (item: any) => item.value ?? item.id,
              )
            : null,
        holidayPayMethod: v.holidayPayMethod?.value || null,
        outsideUKWorker: v.outsideUKWorker ?? null,
        taxCode: v.taxCode ?? '',
        niCategory: v.niCategory?.value || null,
        starterDeclaration: v.starterDeclaration?.value || null,
        p45TaxToDate: v.p45TaxToDate || null,
        taxBasis: v.taxBasis?.value || null,
        starterType: v.starterType?.value || null,
        p45GrossToDate: v.p45GrossToDate || null,
        previousPayrollNo: v.previousPayrollNo || null,
        previousPayrollIdUnknown: v.previousPayrollIdUnknown ?? false,
        payrollApproveNeeded:
          v.payrollApproveNeeded === true
            ? YesNoNAEnum.YES
            : v.payrollApproveNeeded === false
              ? YesNoNAEnum.NO
              : YesNoNAEnum.NOT_AVAILABLE,
        isNIDirector: v.isNIDirector ?? null,
        // NX-5463: these two were rendered in the form but never included in
        // the payload, so every save silently dropped the entered values.
        NIDirectorStartDate:
          v.isNIDirector && v.NIDirectorStartDate
            ? moment(v.NIDirectorStartDate).format('YYYY-MM-DD')
            : null,
        NIDirectorMethod: v.isNIDirector
          ? (v.NIDirectorMethod?.value ?? null)
          : null,
        // Opening Balance (P11)
        p11: {
          eeNI: v.employeeNI || null,
          erNI: v.employerNI || null,
          eePension: v.employeePension || null,
          erPension: v.employerPension || null,
          stLoan: v.studentLoan || null,
          pgLoan: v.postGraduateStudentLoan || null,
          ssp: v.ssp || null,
          smp: v.smp || null,
          sap: v.sap || null,
          spp: v.spp || null,
          sppa: v.sppa || null,
          shpp: v.shpp || null,
          sncp: v.sncp || null,
          grossToLel: v.grossToLel || null,
          grossLelToPt: v.grossLelToPt || null,
          grossPtToUEL: v.grossPtToUEL || null,
          gross: v.gross || null,
          grossForTax: v.grossForTax || null,
          grossForNI: v.grossForNI || null,
          tax: v.tax || null,
          // Director NI Contribution
          ...(isNIMiddleJoined
            ? {
                grossToLelAsDirector: v.grossToLelAsDirector || null,
                grossLelToPtAsDirector: v.grossLelToPtAsDirector || null,
                grossPtToUELAsDirector: v.grossPtToUELAsDirector || null,
                grossForNIAsDirector: v.grossForNIAsDirector || null,
                eeNIAsDirector: v.eeNIAsDirector || null,
                erNIAsDirector: v.erNIAsDirector || null,
              }
            : {}),
        },
      } as any;
    }

    await updateSettingEmployeeById(employeeId, submitParams);
    fetchPayrollSetting(employeeId);
    getEmployeeById(employeeId);
    showMessage({ type: 'success', content: messages['common.updateSuccess'] });
  };

  // NX-4682: in the quick-edit drawer, surface Save in the drawer header
  // (always visible) instead of at the bottom of the form.
  const inDrawer = useDrawerSave(
    handleSave,
    isUpdatingSettingEmployee,
    !readOnly,
  );

  if (!payrollSettingData || isFetchingPayrollSetting || isFetchingEmployee)
    return (
      <div className="relative h-[100px]">
        <AppLoader />
      </div>
    );

  const renderToggle = (name: string, label: string) => (
    <div className="flex items-center justify-between min-h-[40px]">
      <span className="text-[14px] font-medium text-[#5C656E]">{label}</span>
      <Switch
        checked={watchValues?.[name] ?? false}
        onChange={(checked) => form.setFieldValue(name, checked)}
      />
    </div>
  );

  return (
    <div className="relative">
      {isUpdatingSettingEmployee && (
        <div className="absolute inset-0 z-10 bg-white/60">
          <AppLoader />
        </div>
      )}
      <Typography.Title
        level={5}
        className="!mb-8 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.general'] ?? 'General'}
      </Typography.Title>

      <AppFormContainer
        form={form}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        disabled={readOnly}
      >
        {/* ── PAYMENT RULES ── */}
        {!isUK && (
          <Form.Item
            label={messages['common.paymentModel']}
            name="paymentModel"
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            <AppSelect
              options={defaultPaymentModel ?? []}
              className="w-full !min-w-0"
              placeholder={messages['form.select']}
            />
          </Form.Item>
        )}

        {isUKCountry && (
          <>
            {watchValues?.paymentModel?.value &&
              (watchValues.paymentModel.value === PAYMENT_MODEL_ENUM.CIS ||
                watchValues.paymentModel.value ===
                  PAYMENT_MODEL_ENUM.NON_CIS) && (
                <Form.Item
                  label={messages['common.businessType']}
                  name="businessType"
                  rules={[
                    { required: true, message: messages['form.pleaseSelect'] },
                  ]}
                >
                  <AppSelect
                    options={defaultsBusinessTypeOptions}
                    className="w-full !min-w-0"
                    placeholder={messages['form.select']}
                  />
                </Form.Item>
              )}

            {!isBureau && (
              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={messages['common.primaryAgency']}
                    name="primaryAgency"
                  >
                    <AppSelectSync
                      apiType="searchAgency"
                      type="agency"
                      className="w-full !min-w-0"
                      placeholder={messages['form.select']}
                      onChangeValue={(value: any) =>
                        form.setFieldsValue({ primaryAgency: value })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={messages['common.secondaryAgency']}
                    name="secondaryAgency"
                  >
                    <AppSelectSync
                      apiType="searchAgency"
                      type="agency"
                      className="w-full !min-w-0"
                      placeholder={messages['form.select']}
                      onChangeValue={(value: any) =>
                        form.setFieldsValue({ secondaryAgency: value })
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {isCisNonCis ? (
              <>
                {/* CIS/Non-CIS layout: Payment Term / Margin Method */}
                {!isBureau && (
                  <>
                    <Row gutter={24}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={messages['common.paymentTerm']}
                          name="paymentTerm"
                          rules={[
                            {
                              required: true,
                              message: messages['form.pleaseSelect'],
                            },
                          ]}
                        >
                          <AppSelect
                            options={defaultPaymentTerm}
                            className="w-full !min-w-0"
                            placeholder={messages['form.select']}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <MarginMethodField
                          form={form}
                          value={watchValues?.marginMethod}
                          employeeId={employeeId}
                        />
                      </Col>
                    </Row>
                    <Row gutter={24}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={messages['common.margin']}
                          name="margin"
                        >
                          <AppCurrencyInput
                            placeholder={messages['form.enter']}
                            prefix={showCurrencyByCountry(payrollCountry)}
                            disabled={isMarginFromTable}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={messages['common.marginCapped']}
                          name="marginCapped"
                        >
                          <AppCurrencyInput
                            placeholder={messages['form.enter']}
                            prefix={showCurrencyByCountry(payrollCountry)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}
                {watchValues?.marginMethod?.value ===
                  enumMarginMethod.PERCENT_ON_TIMESHEET_VALUE && (
                  <Form.Item
                    label={messages['common.miniumMargin']}
                    name="minMargin"
                  >
                    <AppCurrencyInput
                      placeholder={messages['form.enter']}
                      prefix={showCurrencyByCountry(payrollCountry)}
                    />
                  </Form.Item>
                )}
                {watchValues?.paymentModel?.value ===
                  PAYMENT_MODEL_ENUM.UMBRELLA && (
                  <>
                    <Form.Item name="autoReduceMarginForNMW">
                      <Inheritable<boolean>
                        label={
                          messages['common.autoReduceMarginForNMW'] as string
                        }
                        name="autoReduceMarginForNMW"
                        value={watchValues?.autoReduceMarginForNMW}
                        inheritedValue={false}
                        form={form}
                        disabled={readOnly}
                      >
                        {({ value, onChange, disabled }) => (
                          <Switch
                            checked={value ?? false}
                            disabled={disabled}
                            onChange={onChange}
                          />
                        )}
                      </Inheritable>
                    </Form.Item>
                  </>
                )}
                <Form.Item name="assignmentRequired">
                  {renderToggle(
                    'assignmentRequired',
                    messages['common.assignmentRequired'] as string,
                  )}
                </Form.Item>
                {/* Working Time Regulation only for CIS */}
                {watchValues?.paymentModel?.value ===
                  PAYMENT_MODEL_ENUM.CIS && (
                  <Form.Item name="enableWTR">
                    <Inheritable<boolean>
                      label={
                        messages[
                          'common.enableWorkingTimeRegulationTracking'
                        ] as string
                      }
                      name="enableWTR"
                      value={watchValues?.enableWTR}
                      inheritedValue={false}
                      form={form}
                      disabled={readOnly}
                    >
                      {({ value, onChange, disabled }) => (
                        <Switch
                          checked={value ?? false}
                          disabled={disabled}
                          onChange={onChange}
                        />
                      )}
                    </Inheritable>
                  </Form.Item>
                )}
                <Form.Item
                  name="timesheetApprovalNeeded"
                  wrapperCol={{ span: 24 }}
                >
                  <Inheritable<boolean>
                    label={messages['common.timesheetApprovalNeeded'] as string}
                    name="timesheetApprovalNeeded"
                    value={watchValues?.timesheetApprovalNeeded}
                    inheritedValue={false}
                    form={form}
                    disabled={readOnly}
                  >
                    {({ value, onChange, disabled }) => (
                      <Switch
                        checked={value ?? false}
                        disabled={disabled}
                        onChange={onChange}
                      />
                    )}
                  </Inheritable>
                </Form.Item>
                {watchValues?.timesheetApprovalNeeded === true && (
                  <Form.Item
                    label={messages['common.timesheetApprover']}
                    name="timesheetApprover"
                  >
                    <AppSelectSync
                      type="approver"
                      apiType="searchAgencyUsers"
                      placeholder={messages['form.select']}
                      style={{ width: '100%' }}
                      onChangeValue={(value: any) =>
                        form.setFieldValue('timesheetApprover', value)
                      }
                      searchId={
                        watchValues?.primaryAgency?.value ||
                        watchValues?.secondaryAgency?.value ||
                        payrollSettingData?.primaryAgency?.id ||
                        payrollSettingData?.secondaryAgency?.id
                      }
                      mode="multiple"
                    />
                  </Form.Item>
                )}
              </>
            ) : (
              <>
                {/* Umbrella/PAYEE layout: Payment Term / Restricted Hours */}
                {!isBureau ? (
                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.paymentTerm']}
                        name="paymentTerm"
                        rules={[
                          {
                            required: true,
                            message: messages['form.pleaseSelect'],
                          },
                        ]}
                      >
                        <AppSelect
                          options={defaultPaymentTerm}
                          className="w-full !min-w-0"
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={12}
                      className="flex items-center max-sm:!block"
                    >
                      <Form.Item
                        name="restrictedHours"
                        className="!mb-0 w-full"
                      >
                        <Inheritable<boolean>
                          label={`${messages['common.restrictedHours']}?`}
                          tooltip={messages['common.restrictedHoursTooltip']}
                          name="restrictedHours"
                          value={watchValues?.restrictedHours}
                          inheritedValue={false}
                          form={form}
                          disabled={readOnly}
                        >
                          {({ value, onChange, disabled }) => (
                            <Switch
                              checked={value ?? false}
                              disabled={disabled}
                              onChange={onChange}
                            />
                          )}
                        </Inheritable>
                      </Form.Item>
                    </Col>
                  </Row>
                ) : (
                  <Form.Item name="restrictedHours">
                    <Inheritable<boolean>
                      label={`${messages['common.restrictedHours']}?`}
                      tooltip={messages['common.restrictedHoursTooltip']}
                      name="restrictedHours"
                      value={watchValues?.restrictedHours}
                      inheritedValue={false}
                      form={form}
                      disabled={readOnly}
                    >
                      {({ value, onChange, disabled }) => (
                        <Switch
                          checked={value ?? false}
                          disabled={disabled}
                          onChange={onChange}
                        />
                      )}
                    </Inheritable>
                  </Form.Item>
                )}

                {!isBureau && (
                  <>
                    <Row gutter={24}>
                      <Col xs={24} sm={12}>
                        <MarginMethodField
                          form={form}
                          value={watchValues?.marginMethod}
                          employeeId={employeeId}
                        />
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={messages['common.margin']}
                          name="margin"
                        >
                          <AppCurrencyInput
                            placeholder={messages['form.enter']}
                            prefix={showCurrencyByCountry(payrollCountry)}
                            disabled={isMarginFromTable}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item
                      label={messages['common.marginCapped']}
                      name="marginCapped"
                    >
                      <AppCurrencyInput
                        placeholder={messages['form.enter']}
                        prefix={showCurrencyByCountry(payrollCountry)}
                      />
                    </Form.Item>
                  </>
                )}
                {watchValues?.marginMethod?.value ===
                  enumMarginMethod.PERCENT_ON_TIMESHEET_VALUE && (
                  <Form.Item
                    label={messages['common.miniumMargin']}
                    name="minMargin"
                  >
                    <AppCurrencyInput
                      placeholder={messages['form.enter']}
                      prefix={showCurrencyByCountry(payrollCountry)}
                    />
                  </Form.Item>
                )}
                {watchValues?.paymentModel?.value ===
                  PAYMENT_MODEL_ENUM.UMBRELLA && (
                  <>
                    <Form.Item name="autoReduceMarginForNMW">
                      <Inheritable<boolean>
                        label={
                          messages['common.autoReduceMarginForNMW'] as string
                        }
                        name="autoReduceMarginForNMW"
                        value={watchValues?.autoReduceMarginForNMW}
                        inheritedValue={false}
                        form={form}
                        disabled={readOnly}
                      >
                        {({ value, onChange, disabled }) => (
                          <Switch
                            checked={value ?? false}
                            disabled={disabled}
                            onChange={onChange}
                          />
                        )}
                      </Inheritable>
                    </Form.Item>
                  </>
                )}
                <Form.Item name="enableWTR">
                  <Inheritable<boolean>
                    label={
                      messages[
                        'common.enableWorkingTimeRegulationTracking'
                      ] as string
                    }
                    name="enableWTR"
                    value={watchValues?.enableWTR}
                    inheritedValue={false}
                    form={form}
                    disabled={readOnly}
                  >
                    {({ value, onChange, disabled }) => (
                      <Switch
                        checked={value ?? false}
                        disabled={disabled}
                        onChange={onChange}
                      />
                    )}
                  </Inheritable>
                </Form.Item>
              </>
            )}
          </>
        )}

        {isUK && (
          <>
            <Form.Item name="timesheetApprovalNeeded" wrapperCol={{ span: 24 }}>
              <Inheritable<boolean>
                label={messages['common.timesheetApprovalNeeded'] as string}
                name="timesheetApprovalNeeded"
                value={watchValues?.timesheetApprovalNeeded}
                inheritedValue={false}
                form={form}
                disabled={readOnly}
              >
                {({ value, onChange, disabled }) => (
                  <Switch
                    checked={value ?? false}
                    disabled={disabled}
                    onChange={onChange}
                  />
                )}
              </Inheritable>
            </Form.Item>
            {watchValues?.timesheetApprovalNeeded === true && (
              <Form.Item
                label={messages['common.timesheetApprover']}
                name="listEmprApprovers"
              >
                <AppSelectSync
                  type="approver"
                  apiType="searchUserAdminCompany"
                  placeholder={messages['form.select']}
                  style={{ width: '100%' }}
                  onChangeValue={(value: any) =>
                    form.setFieldValue('listEmprApprovers', value)
                  }
                  paramFilter={{
                    companyUserIds: employeeData?.companyPayment?.id
                      ? [employeeData?.companyPayment?.id]
                      : undefined,
                    roles: ['company'],
                  }}
                  mode="multiple"
                />
              </Form.Item>
            )}
          </>
        )}

        {/* Ireland-specific payment rule fields */}
        {isIreland && (
          <>
            {/*
              Umbrella-Ireland only: margin/agency are a product concept (the
              umbrella payment model), not a country one, so they are
              re-homed here rather than in the UK-only isUKCountry block
              above. Same field `name`s as that block → no payload-mapping
              change, and handleSave's `isIreland` branch already spreads
              them when `!isBureau`.

              Gated on the product alone, not on `isUmbrellaModel`. In an
              umbrella tenant every worker is placed through an agency and
              carries a margin whatever their payment model, so keying these
              off the UMBRELLA model left Ireland's PAYE and RCT workers with
              no way to see or set Primary Agency, Secondary Agency, Payment
              Term or Margin. The stored values survived — the form is seeded
              from payrollSettingData and handleSave round-trips them for every
              `!isBureau` record — so this was unreachable UI, not data loss.
            */}
            {!isBureau && (
              <>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.primaryAgency']}
                      name="primaryAgency"
                    >
                      <AppSelectSync
                        apiType="searchAgency"
                        type="agency"
                        className="w-full !min-w-0"
                        placeholder={messages['form.select']}
                        onChangeValue={(value: any) =>
                          form.setFieldsValue({ primaryAgency: value })
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.secondaryAgency']}
                      name="secondaryAgency"
                    >
                      <AppSelectSync
                        apiType="searchAgency"
                        type="agency"
                        className="w-full !min-w-0"
                        placeholder={messages['form.select']}
                        onChangeValue={(value: any) =>
                          form.setFieldsValue({ secondaryAgency: value })
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.paymentTerm']}
                      name="paymentTerm"
                      rules={[
                        {
                          required: true,
                          message: messages['form.pleaseSelect'],
                        },
                      ]}
                    >
                      <AppSelect
                        options={defaultPaymentTerm}
                        className="w-full !min-w-0"
                        placeholder={messages['form.select']}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <MarginMethodField
                      form={form}
                      value={watchValues?.marginMethod}
                      employeeId={employeeId}
                    />
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item label={messages['common.margin']} name="margin">
                      <AppCurrencyInput
                        placeholder={messages['form.enter']}
                        prefix={showCurrencyByCountry(payrollCountry)}
                        disabled={isMarginFromTable}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.marginCapped']}
                      name="marginCapped"
                    >
                      <AppCurrencyInput
                        placeholder={messages['form.enter']}
                        prefix={showCurrencyByCountry(payrollCountry)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                {watchValues?.marginMethod?.value ===
                  enumMarginMethod.PERCENT_ON_TIMESHEET_VALUE && (
                  <Form.Item
                    label={messages['common.miniumMargin']}
                    name="minMargin"
                  >
                    <AppCurrencyInput
                      placeholder={messages['form.enter']}
                      prefix={showCurrencyByCountry(payrollCountry)}
                    />
                  </Form.Item>
                )}
              </>
            )}
            <Form.Item name="timesheetApprovalNeeded" wrapperCol={{ span: 24 }}>
              <Inheritable<boolean>
                label={messages['common.timesheetApprovalNeeded'] as string}
                name="timesheetApprovalNeeded"
                value={watchValues?.timesheetApprovalNeeded}
                inheritedValue={false}
                form={form}
                disabled={readOnly}
              >
                {({ value, onChange, disabled }) => (
                  <Switch
                    checked={value ?? false}
                    disabled={disabled}
                    onChange={onChange}
                  />
                )}
              </Inheritable>
            </Form.Item>
            {watchValues?.timesheetApprovalNeeded === true && (
              <Form.Item
                label={messages['common.timesheetApprover']}
                name="listEmprApprovers"
              >
                <AppSelectSync
                  type="approver"
                  apiType="searchUserAdminCompany"
                  placeholder={messages['form.select']}
                  style={{ width: '100%' }}
                  onChangeValue={(value: any) =>
                    form.setFieldValue('listEmprApprovers', value)
                  }
                  paramFilter={{
                    companyUserIds: employeeData?.companyPayment?.id
                      ? [employeeData?.companyPayment?.id]
                      : undefined,
                    roles: ['company'],
                  }}
                  mode="multiple"
                />
              </Form.Item>
            )}
            <Form.Item name="payrollApproveNeeded" wrapperCol={{ span: 24 }}>
              <Inheritable<boolean>
                label={messages['common.payrollApprovalNeeded'] as string}
                name="payrollApproveNeeded"
                value={watchValues?.payrollApproveNeeded}
                inheritedValue={false}
                form={form}
                disabled={readOnly}
              >
                {({ value, onChange, disabled }) => (
                  <Switch
                    checked={value ?? false}
                    disabled={disabled}
                    onChange={onChange}
                  />
                )}
              </Inheritable>
            </Form.Item>
            {isPayeModel && (
              <>
                <Form.Item
                  label={messages['common.directorship']}
                  name="directorShip"
                >
                  <AppSelect
                    options={directorshipOptions}
                    className="w-full !min-w-0"
                    placeholder={messages['form.select']}
                  />
                </Form.Item>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.holidayPayMethod']}
                      name="holidayPayMethod"
                    >
                      <AppSelect
                        options={getHolidayPaymentMethodOptions(isIreland)}
                        className="w-full !min-w-0"
                        placeholder={messages['form.select']}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.holidayPayRate']}
                      name="holidayPayRate"
                    >
                      <AppPercentageInput
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
            {isRctModel && (
              <>
                <div className="h-[8px] w-full rounded-[4px] bg-[#EFEFEF] my-6" />
                <Typography.Text className="!text-[14px] !font-semibold !text-[#6F767E] !uppercase !tracking-wider !mb-4 !block">
                  {messages['common.taxSettings']}
                </Typography.Text>
                <Form.Item
                  label={messages['common.rctStatus'] ?? 'RCT Status'}
                  name="cisStatus"
                >
                  <AppSelect
                    options={rctStatusOptions}
                    className="w-full !min-w-0"
                    placeholder={messages['form.select']}
                  />
                </Form.Item>
                <Form.Item
                  label={
                    messages['common.subcontractorRefNumber'] ??
                    'Subcontractor Reference Number'
                  }
                  name="subcontractorRefNumber"
                >
                  <AppInput placeholder={messages['form.enter']} />
                </Form.Item>
              </>
            )}
          </>
        )}

        {/* UK Bureau extra fields */}
        {isUK && (
          <>
            <Form.Item name="payrollApproveNeeded" wrapperCol={{ span: 24 }}>
              <Inheritable<boolean>
                label={messages['common.payrollApprovalNeeded'] as string}
                name="payrollApproveNeeded"
                value={watchValues?.payrollApproveNeeded}
                inheritedValue={false}
                form={form}
                disabled={readOnly}
              >
                {({ value, onChange, disabled }) => (
                  <Switch
                    checked={value ?? false}
                    disabled={disabled}
                    onChange={onChange}
                  />
                )}
              </Inheritable>
            </Form.Item>
            <Form.Item name="isNIDirector">
              {renderToggle('isNIDirector', messages['common.isDirector'])}
            </Form.Item>
            {watchValues?.isNIDirector && (
              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={
                      messages['common.directorStartDate'] ??
                      'Director Start Date'
                    }
                    name="NIDirectorStartDate"
                  >
                    <AppDatePicker
                      placeholder={messages['form.selectDate']}
                      format="DD/MM/YYYY"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={
                      messages['common.niCalculationMethod'] ??
                      'NI Calculation Method'
                    }
                    name="NIDirectorMethod"
                    rules={[
                      {
                        required: true,
                        message: messages['form.pleaseSelect'],
                      },
                    ]}
                  >
                    <AppSelect
                      options={NIDirectorMethodOptions}
                      className="w-full !min-w-0"
                      placeholder={messages['form.select']}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </>
        )}

        {/* ── CIS/Non-CIS TAX SETTINGS ── */}
        {isUKCountry && isCisNonCis && (
          <>
            <div className="h-[8px] w-full rounded-[4px] bg-[#EFEFEF] my-6" />
            <Typography.Text className="!text-[14px] !font-semibold !text-[#6F767E] !uppercase !tracking-wider !mb-4 !block">
              {messages['common.taxSettings']}
            </Typography.Text>

            {watchValues?.paymentModel?.value === PAYMENT_MODEL_ENUM.CIS && (
              <>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={`${messages['common.utrNo'] ?? 'UTR No'} (${messages['common.upTo13Digits'] ?? 'Upto 13 digits'})`}
                      name="UTRNo"
                    >
                      <AppInput
                        placeholder={messages['form.enter']}
                        maxLength={13}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.cisStatus']}
                      name="cisStatus"
                    >
                      <AppSelect
                        options={defaultCisStatus}
                        className="w-full !min-w-0"
                        placeholder={messages['form.select']}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.personalVerificationNumber']}
                      name="personalVerificationNumber"
                    >
                      <AppInput placeholder="e.g. 1234567" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.verificationDate']}
                      name="verificationDate"
                    >
                      <AppDatePicker
                        className="w-full !min-w-0"
                        placeholder={messages['form.selectDate']}
                        format="DD/MM/YYYY"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.matchDate']}
                      name="matchDate"
                    >
                      <AppDatePicker
                        className="w-full !min-w-0"
                        placeholder={messages['form.selectDate']}
                        format="DD/MM/YYYY"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.vatRegNo']}
                      name="VATRegNo"
                    >
                      <AppInput
                        placeholder={messages['form.enter']}
                        maxLength={9}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}

            {watchValues?.paymentModel?.value === PAYMENT_MODEL_ENUM.NON_CIS &&
              watchValues?.businessType?.value !==
                BusinessTypeEnum.PARTNERSHIP && (
                <>
                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={`${messages['common.utrNo'] ?? 'UTR No'} (${messages['common.upTo13Digits'] ?? 'Upto 13 digits'})`}
                        name="UTRNo"
                      >
                        <AppInput
                          placeholder={messages['form.enter']}
                          maxLength={13}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={messages['common.vatRegNo']}
                        name="VATRegNo"
                      >
                        <AppInput
                          placeholder={messages['form.enter']}
                          maxLength={9}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}

            {/* Company business type */}
            {watchValues?.businessType?.value === BusinessTypeEnum.COMPANY && (
              <>
                <Form.Item
                  label={
                    isBureau
                      ? messages['common.bureau.companyName']
                      : messages['common.companyName']
                  }
                  name="companyName"
                  rules={[
                    { required: true, message: messages['form.pleaseEnter'] },
                  ]}
                >
                  <AppInput placeholder={messages['form.enter']} />
                </Form.Item>
                <Form.Item
                  label={messages['common.registrationNumber']}
                  name="registrationNumber"
                  rules={[
                    { required: true, message: messages['form.pleaseEnter'] },
                  ]}
                >
                  <AppInput placeholder={messages['form.enter']} />
                </Form.Item>
                {watchValues?.paymentModel?.value ===
                  PAYMENT_MODEL_ENUM.CIS && (
                  <Form.Item
                    label={messages['common.vatNumber'] ?? 'VAT Number'}
                    name="VATRegNo"
                  >
                    <AppInput
                      placeholder={messages['form.enter']}
                      maxLength={9}
                    />
                  </Form.Item>
                )}
              </>
            )}

            {/* Partnership business type */}
            {watchValues?.businessType?.value ===
              BusinessTypeEnum.PARTNERSHIP && (
              <>
                <Form.Item
                  label={messages['common.firmName']}
                  name="firmName"
                  rules={[
                    { required: true, message: messages['form.pleaseEnter'] },
                  ]}
                >
                  <AppInput placeholder="e.g. Google" />
                </Form.Item>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.partnersName']}
                      name="partnersName"
                      rules={[
                        {
                          required: true,
                          message: messages['form.pleaseEnter'],
                        },
                      ]}
                    >
                      <AppInput placeholder="e.g. Google" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.partnersCategory']}
                      name="partnersCategory"
                      rules={[
                        {
                          required: true,
                          message: messages['form.pleaseSelect'],
                        },
                      ]}
                    >
                      <AppSelect
                        options={defaultPartnerCategoryOptions}
                        className="w-full !min-w-0"
                        placeholder={messages['form.select']}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                {watchValues?.paymentModel?.value ===
                  PAYMENT_MODEL_ENUM.CIS && (
                  <Form.Item
                    label={messages['common.vatNumber'] ?? 'VAT Number'}
                    name="VATRegNo"
                  >
                    <AppInput
                      placeholder={messages['form.enter']}
                      maxLength={9}
                    />
                  </Form.Item>
                )}
              </>
            )}

            <Form.Item name="domesticReverseChargeApplicable">
              <Inheritable<boolean>
                label={
                  messages['common.domesticReverseChargeApplicable'] as string
                }
                name="domesticReverseChargeApplicable"
                value={watchValues?.domesticReverseChargeApplicable}
                inheritedValue={false}
                form={form}
                disabled={readOnly}
              >
                {({ value, onChange, disabled }) => (
                  <Switch
                    checked={value ?? false}
                    disabled={disabled}
                    onChange={onChange}
                  />
                )}
              </Inheritable>
            </Form.Item>
          </>
        )}

        {/* ── TAX, NI AND STARTER DECLARATION ── */}
        {isUKCountry &&
          watchValues?.paymentModel?.value !== PAYMENT_MODEL_ENUM.CIS &&
          watchValues?.paymentModel?.value !== PAYMENT_MODEL_ENUM.NON_CIS && (
            <>
              <div className="h-[8px] w-full rounded-[4px] bg-[#EFEFEF] my-6" />
              <div className="flex items-center justify-between mb-4">
                <Typography.Text className="!text-[14px] !font-semibold !text-[#6F767E] !uppercase !tracking-wider">
                  {messages['common.taxNIAndStarterDeclaration']}
                </Typography.Text>
              </div>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={messages['common.taxCode']}
                    name="taxCode"
                    rules={[
                      { required: true, message: messages['form.pleaseEnter'] },
                      taxCodeValidationRule(messages),
                    ]}
                  >
                    <AppInput
                      upperCaseAll
                      placeholder={messages['form.enter']}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={messages['common.taxBasis']}
                    name="taxBasis"
                  >
                    <AppSelect
                      options={defaultTaxBasisOptions}
                      className="w-full !min-w-0"
                      placeholder={messages['form.select']}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={getFieldLabelByCountry(payrollCountry, 'NINo')}
                    name="niNumber"
                  >
                    <AppInput placeholder={messages['form.enter']} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={messages['common.niCategory']}
                    name="niCategory"
                    rules={[
                      {
                        required: true,
                        message: messages['form.pleaseSelect'],
                      },
                    ]}
                  >
                    <AppSelect
                      options={NICategoryOptions}
                      className="w-full !min-w-0"
                      placeholder={messages['form.select']}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Workplace Postcode - required for freeport NI categories */}
              {freeportNIcategories.some(
                (f) => f.value === watchValues?.niCategory?.value,
              ) && (
                <Form.Item
                  label={
                    messages['common.workplacePostcode'] ?? 'Workplace Postcode'
                  }
                  name="workplacePostcode"
                  rules={[
                    {
                      required: true,
                      message: messages['form.pleaseEnter'],
                    },
                  ]}
                >
                  <AppInput
                    placeholder={messages['form.enter']}
                    maxLength={8}
                    upperCaseAll
                  />
                </Form.Item>
              )}

              <Form.Item
                label={messages['common.starterType']}
                name="starterType"
                rules={[
                  {
                    required: true,
                    message: messages['form.pleaseSelect'],
                  },
                ]}
              >
                <AppSelect
                  options={starterTypeOptions}
                  className="w-full !min-w-0"
                  placeholder={messages['form.select']}
                />
              </Form.Item>

              <Form.Item
                label={messages['common.starterDeclaration']}
                name="starterDeclaration"
                rules={[
                  { required: true, message: messages['form.pleaseSelect'] },
                ]}
              >
                <AppSelect
                  options={starterDeclarationOptions}
                  className="w-full !min-w-0"
                  placeholder={messages['form.select']}
                />
              </Form.Item>

              {/* ── PREVIOUS PAYROLL & P45 ── */}
              {!isIreland && (
                <>
                  <div className="h-[4px] w-full bg-neutral-100 rounded-full my-6" />

                  {/* Previous Payroll No */}
                  <Form.Item
                    label={
                      messages['common.previousPayrollNo'] ??
                      'Previous Payroll No'
                    }
                    name="previousPayrollNo"
                  >
                    <AppInput
                      placeholder={messages['form.enter']}
                      disabled={previousPayrollIdUnknown === true}
                    />
                  </Form.Item>

                  {/* Previous Payroll ID Unknown */}
                  <div className="flex items-center justify-between mb-6">
                    <span>
                      {messages['common.previousPayrollIdUnknown'] ??
                        'Previous Payroll ID Unknown'}
                    </span>
                    <Form.Item
                      name="previousPayrollIdUnknown"
                      valuePropName="checked"
                      className="!mb-0"
                    >
                      <AppSwitch
                        onChange={(checked: boolean) => {
                          if (checked) {
                            form.setFieldValue('previousPayrollNo', '');
                          }
                        }}
                      />
                    </Form.Item>
                  </div>

                  {/* Previous Job Tax Information (P45/P60) */}
                  <div className="text-[16px] mb-1">
                    {messages['common.previousJobTaxInformationP45P60'] ??
                      'Previous job tax information (P45, P60)'}
                  </div>
                  <div className="text-[14px] text-[#71717A] mb-6">
                    {messages[
                      'common.previousJobTaxInformationP45P60HelperText'
                    ] ??
                      "Entering or changing previous job tax information might affect the employee's tax calculation"}
                  </div>

                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={
                          <div>
                            <div>{messages['common.totalPayToDate']}</div>
                            <div className="text-[12px] text-[#71717A] font-normal">
                              {messages['common.fromPreviousEmployer'] ??
                                'from previous employer'}
                            </div>
                          </div>
                        }
                        name="p45GrossToDate"
                      >
                        <AppCurrencyInput
                          prefix={showCurrencyByCountry(payrollCountry)}
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={
                          <div>
                            <div>{messages['common.totalTaxToDate']}</div>
                            <div className="text-[12px] text-[#71717A] font-normal">
                              {messages['common.fromPreviousEmployer'] ??
                                'from previous employer'}
                            </div>
                          </div>
                        }
                        name="p45TaxToDate"
                      >
                        <AppCurrencyInput
                          prefix={showCurrencyByCountry(payrollCountry)}
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Opening Balance Button */}
                  <AppButton
                    variant="outlined"
                    leftIcon={
                      <Icons.IconFilePlus
                        color={defaultTheme.theme.palette.brand}
                      />
                    }
                    name={
                      messages['common.openingBalance'] ?? 'Opening Balance'
                    }
                    className="mb-6"
                    disabled={
                      (!previousPayrollNo && !previousPayrollIdUnknown) ||
                      watchValues?.starterType?.value !== 'EXISTING'
                    }
                    onClick={() => setShowOpeningBalance(!showOpeningBalance)}
                  />

                  {/* ===== Opening Balance Sections (toggle) ===== */}
                  {showOpeningBalance && (
                    <>
                      {/* Year to Date Figures */}
                      <div className="h-[4px] w-full bg-neutral-100 rounded-full mb-6" />
                      <div className="text-[16px] mb-4">
                        {messages['common.yearToDateFigures'] ??
                          'Year to Date Figures'}
                      </div>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.employeeNI']}
                            name="employeeNI"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.employerNI']}
                            name="employerNI"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.employeePension']}
                            name="employeePension"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.employerPension']}
                            name="employerPension"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.studentLoan']}
                            name="studentLoan"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.postGraduateStudentLoan']}
                            name="postGraduateStudentLoan"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* Statutories Year to Date */}
                      <div className="h-[4px] w-full bg-neutral-100 rounded-full mb-6" />
                      <div className="text-[16px] mb-4">
                        {messages['common.statutoryYearToDate'] ??
                          'Statutories Year to Date'}
                      </div>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.smp'] ?? 'SMP'}
                            name="smp"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.spp'] ?? 'SPP'}
                            name="spp"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.sap'] ?? 'SAP'}
                            name="sap"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.shpp'] ?? 'ShPP'}
                            name="shpp"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.ssp'] ?? 'SSP'}
                            name="ssp"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.sncp'] ?? 'SNCP'}
                            name="sncp"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* Calculation Breakdown Year to Date */}
                      <div className="h-[4px] w-full bg-neutral-100 rounded-full mb-6" />
                      <div className="text-[16px] mb-4">
                        {messages['common.calculationBreakdownYearToDate'] ??
                          'Calculation Breakdown Year to Date'}
                      </div>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.grossToLel']}
                            name="grossToLel"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.grossLelToPt']}
                            name="grossLelToPt"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.grossPtToUEL']}
                            name="grossPtToUEL"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.gross'] ?? 'Gross'}
                            name="gross"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.taxableGross']}
                            name="grossForTax"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label={messages['common.NIableGross']}
                            name="grossForNI"
                          >
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <Form.Item label={messages['common.tax']} name="tax">
                            <AppCurrencyInput
                              placeholder={messages['form.enter']}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* Director NI Contribution (only when employee is a Director) */}
                      {isNIMiddleJoined && (
                        <>
                          <div className="h-[4px] w-full bg-neutral-100 rounded-full mb-6" />
                          <div className="text-[16px] mb-4">
                            {messages['common.niContributionAsDirector'] ??
                              'NI Contribution as Director'}
                          </div>
                          <Row gutter={24}>
                            <Col xs={24} sm={12}>
                              <Form.Item
                                label={`${messages['common.grossToLel'] ?? 'Gross to LEL'} (as director)`}
                                name="grossToLelAsDirector"
                              >
                                <AppCurrencyInput
                                  placeholder={messages['form.enter']}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                              <Form.Item
                                label={`${messages['common.grossLelToPt'] ?? 'Gross LEL to PT'} (as director)`}
                                name="grossLelToPtAsDirector"
                              >
                                <AppCurrencyInput
                                  placeholder={messages['form.enter']}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={24}>
                            <Col xs={24} sm={12}>
                              <Form.Item
                                label={`${messages['common.grossPtToUEL'] ?? 'Gross PT to UEL'} (as director)`}
                                name="grossPtToUELAsDirector"
                              >
                                <AppCurrencyInput
                                  placeholder={messages['form.enter']}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                              <Form.Item
                                label={`${messages['common.grossForNI'] ?? 'NIable Gross'} (as director)`}
                                name="grossForNIAsDirector"
                              >
                                <AppCurrencyInput
                                  placeholder={messages['form.enter']}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={24}>
                            <Col xs={24} sm={12}>
                              <Form.Item
                                label={`${messages['common.employeeNI'] ?? 'Employee NI'} (as director)`}
                                name="eeNIAsDirector"
                              >
                                <AppCurrencyInput
                                  placeholder={messages['form.enter']}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                              <Form.Item
                                label={`${messages['common.employerNI'] ?? 'Employer NI'} (as director)`}
                                name="erNIAsDirector"
                              >
                                <AppCurrencyInput
                                  placeholder={messages['form.enter']}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        {/* ── IRELAND TAX/PRSI/USC SECTIONS (editable) ── */}
        {isIreland && isPayeModel && (
          <>
            {/* Employment */}
            <div className="h-[8px] w-full rounded-[4px] bg-[#EFEFEF] my-6" />
            <Typography.Text className="!text-[14px] !font-semibold !text-[#6F767E] !uppercase !tracking-wider !mb-4 !block">
              {messages['common.employment']}
            </Typography.Text>
            <Form.Item
              label={messages['common.employmentStatus']}
              name="employmentStatus"
            >
              <AppSelect
                options={employmentStatusOptions ?? []}
                className="w-full !min-w-0"
                placeholder={messages['form.select']}
              />
            </Form.Item>
            <Form.Item
              label={messages['common.ppsNumber']}
              name="ppsNumber"
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value) return;
                    if (!isValidPPSNumber(value)) {
                      throw new Error(
                        messages['form.invalidPPSNumber'] ??
                          'PPS Number format must be 7 digits followed by 1 or 2 letters',
                      );
                    }
                  },
                },
              ]}
            >
              <AppInput
                className="w-full !min-w-0"
                placeholder={messages['form.enter']}
                upperCaseAll
                maxLength={9}
              />
            </Form.Item>
            <Form.Item
              label={messages['common.employmentId']}
              name="employmentId"
            >
              <AppInput
                className="w-full !min-w-0"
                placeholder={messages['form.enter']}
              />
            </Form.Item>

            {/* Tax */}
            <div className="h-[8px] w-full rounded-[4px] bg-[#EFEFEF] my-6" />
            <Typography.Text className="!text-[14px] !font-semibold !text-[#6F767E] !uppercase !tracking-wider !mb-4 !block">
              {messages['common.tax']}
            </Typography.Text>
            <Form.Item label={messages['common.taxBasis']} name="taxBasis">
              <AppSelect
                options={defaultTaxBasisIrelandOptions ?? []}
                className="w-full !min-w-0"
                placeholder={messages['form.select']}
              />
            </Form.Item>
            <Form.Item label={messages['common.exclusion']} name="taxExclusion">
              <AppSelect
                options={defaultTaxExclusionOptions ?? []}
                className="w-full !min-w-0"
                placeholder={messages['form.select']}
              />
            </Form.Item>
            <div className="h-[1px] w-full bg-neutral-200 mb-[24px]" />
            <div className="text-[16px] mb-[24px]">
              {messages['common.credit']}
            </div>
            <Form.Item label={frequencyLabel} name="taxCreditFrequency">
              <AppCurrencyInput
                prefix={showCurrencyByCountry(payrollCountry)}
                className="w-full !min-w-0"
                placeholder={messages['form.enter']}
                onChange={(val) => {
                  form.setFieldValue('taxCreditFrequency' as any, val);
                  form.setFieldValue(
                    'taxCreditAnnual',
                    calcUSCRateAmount(
                      employeeData?.payFrequency || 'WEEKLY',
                      toNumber(val),
                    ),
                  );
                }}
              />
            </Form.Item>
            <Form.Item label={messages['common.annual']} name="taxCreditAnnual">
              <AppCurrencyInput
                prefix={showCurrencyByCountry(payrollCountry)}
                className="w-full !min-w-0"
                placeholder={messages['form.enter']}
                onChange={(val) => {
                  form.setFieldValue('taxCreditAnnual', val);
                  form.setFieldValue(
                    'taxCreditFrequency' as any,
                    calcUSCRateFrequency(
                      employeeData?.payFrequency || 'WEEKLY',
                      toNumber(val),
                    ),
                  );
                }}
              />
            </Form.Item>
            <div className="h-[1px] w-full bg-neutral-200 mb-[24px]" />
            <div className="text-[16px] mb-[24px]">
              {messages['common.cutOffPoint']}
            </div>
            <Form.Item
              label={frequencyLabel}
              name="taxRateCutOffPointsFrequency"
            >
              <AppCurrencyInput
                prefix={showCurrencyByCountry(payrollCountry)}
                className="w-full !min-w-0"
                placeholder={messages['form.enter']}
                onChange={(val) => {
                  form.setFieldValue(
                    'taxRateCutOffPointsFrequency' as any,
                    val,
                  );
                  form.setFieldValue(
                    'taxRateCutOffPoints',
                    calcUSCRateAmount(
                      employeeData?.payFrequency || 'WEEKLY',
                      toNumber(val),
                    ),
                  );
                }}
              />
            </Form.Item>
            <Form.Item
              label={messages['common.annual']}
              name="taxRateCutOffPoints"
            >
              <AppCurrencyInput
                prefix={showCurrencyByCountry(payrollCountry)}
                className="w-full !min-w-0"
                placeholder={messages['form.enter']}
                onChange={(val) => {
                  form.setFieldValue('taxRateCutOffPoints', val);
                  form.setFieldValue(
                    'taxRateCutOffPointsFrequency' as any,
                    calcUSCRateFrequency(
                      employeeData?.payFrequency || 'WEEKLY',
                      toNumber(val),
                    ),
                  );
                }}
              />
            </Form.Item>

            {/* USC */}
            <div className="h-[8px] w-full rounded-[4px] bg-[#EFEFEF] my-6" />
            <Typography.Text className="!text-[14px] !font-semibold !text-[#6F767E] !uppercase !tracking-wider !mb-4 !block">
              {messages['common.usc']}
            </Typography.Text>
            <Form.Item label={messages['common.exemption']} name="uscExemption">
              <AppSelect
                options={defaultUSCExemptionOptions ?? []}
                className="w-full !min-w-0"
                placeholder={messages['form.select']}
              />
            </Form.Item>
            <Form.Item
              label={messages['common.uscCutOffPoints']}
              name="cutOffPointsType"
            >
              <AppSelect
                options={defaultUSCTiersOptions ?? []}
                className="w-full !min-w-0"
                placeholder={messages['form.select']}
              />
            </Form.Item>
            {USCRateList[
              watchValues?.cutOffPointsType?.value as keyof typeof USCRateList
            ]?.map((rate, index) => {
              const isHighestPoint =
                index ===
                USCRateList[
                  watchValues?.cutOffPointsType
                    ?.value as keyof typeof USCRateList
                ].length -
                  1;
              return (
                <div key={rate.key}>
                  <div className="h-[1px] w-full bg-neutral-200 mb-[24px]" />
                  <div className="text-[16px] mb-[24px]">{rate.label}</div>
                  <Form.Item
                    label={frequencyLabel}
                    name={`${rate.key}Frequency`}
                  >
                    <AppCurrencyInput
                      prefix={showCurrencyByCountry(payrollCountry)}
                      className="w-full !min-w-0"
                      placeholder={messages['form.enter']}
                      onChange={(val) => {
                        form.setFieldValue(`${rate.key}Frequency` as any, val);
                        form.setFieldValue(
                          rate.key as any,
                          calcUSCRateAmount(
                            employeeData?.payFrequency || 'WEEKLY',
                            toNumber(val),
                          ),
                        );
                      }}
                      {...(isHighestPoint && {
                        placeholder: messages['common.balance'],
                        disabled: true,
                      })}
                    />
                  </Form.Item>
                  <Form.Item label={messages['common.annual']} name={rate.key}>
                    <AppCurrencyInput
                      prefix={showCurrencyByCountry(payrollCountry)}
                      className="w-full !min-w-0"
                      placeholder={messages['form.enter']}
                      onChange={(val) => {
                        form.setFieldValue(rate.key as any, val);
                        form.setFieldValue(
                          `${rate.key}Frequency` as any,
                          calcUSCRateFrequency(
                            employeeData?.payFrequency || 'WEEKLY',
                            toNumber(val),
                          ),
                        );
                      }}
                      {...(isHighestPoint && {
                        placeholder: messages['common.balance'],
                        disabled: true,
                      })}
                    />
                  </Form.Item>
                </div>
              );
            })}

            {/* PRSI */}
            <div className="h-[8px] w-full rounded-[4px] bg-[#EFEFEF] my-6" />
            <Typography.Text className="!text-[14px] !font-semibold !text-[#6F767E] !uppercase !tracking-wider !mb-4 !block">
              {messages['common.prsi']}
            </Typography.Text>
            <Form.Item label={messages['common.class']} name="prsiEeClasses">
              <AppSelect
                options={defaultPRSIEeClassesOptions}
                className="w-full !min-w-0"
                placeholder={messages['form.select']}
                optionRender={(option: any) => (
                  <div>
                    <div>{option.label}</div>
                    <small className="text-muted">{option.data?.desc}</small>
                  </div>
                )}
              />
            </Form.Item>
            <Form.Item
              label={messages['common.exemption']}
              name="prsiExemption"
            >
              <AppSelect
                options={defaultPRSIExemptionOptions ?? []}
                className="w-full !min-w-0"
                placeholder={messages['form.select']}
              />
            </Form.Item>

            {/* Other */}
            <div className="h-[8px] w-full rounded-[4px] bg-[#EFEFEF] my-6" />
            <Typography.Text className="!text-[14px] !font-semibold !text-[#6F767E] !uppercase !tracking-wider !mb-4 !block">
              {messages['common.other']}
            </Typography.Text>
            <Form.Item
              label={messages['common.totalLPTToBeDeducted']}
              name="totalLPTToBeDeducted"
            >
              <AppCurrencyInput
                prefix={showCurrencyByCountry(payrollCountry)}
                className="w-full !min-w-0"
                placeholder={messages['form.enter']}
              />
            </Form.Item>
            <Form.Item
              label={messages['common.ascCalculationMethod']}
              name="ascCalculationMethod"
            >
              <AppSelect
                options={defaultASCCalculationMethodOptions ?? []}
                className="w-full !min-w-0"
                placeholder={messages['form.select']}
              />
            </Form.Item>

            {/* ===== Opening Balance (Ireland) ===== */}
            <AppButton
              variant="outlined"
              leftIcon={
                <Icons.IconFilePlus color={defaultTheme.theme.palette.brand} />
              }
              name={messages['common.openingBalance'] ?? 'Opening Balance'}
              className="mb-6 mt-6"
              onClick={() => setShowOpeningBalance(!showOpeningBalance)}
            />

            {showOpeningBalance && (
              <>
                {/* Year to Date Figures */}
                <div className="h-[4px] w-full bg-neutral-100 rounded-full mb-6" />
                <div className="text-[16px] mb-4">
                  {messages['common.yearToDateFigures'] ??
                    'Year to Date Figures'}
                </div>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.taxableGross'] ?? 'Taxable Gross'}
                      name="grossForTax"
                    >
                      <AppCurrencyInput
                        prefix={showCurrencyByCountry(payrollCountry)}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.tax'] ?? 'Tax'}
                      name="tax"
                    >
                      <AppCurrencyInput
                        prefix={showCurrencyByCountry(payrollCountry)}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={
                        messages['common.eePrsiableGross'] ??
                        'Employee PRSIable Gross'
                      }
                      name="eePrsiGross"
                    >
                      <AppCurrencyInput
                        prefix={showCurrencyByCountry(payrollCountry)}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.eePrsi'] ?? 'Employee PRSI'}
                      name="eePRSI"
                    >
                      <AppCurrencyInput
                        prefix={showCurrencyByCountry(payrollCountry)}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={
                        messages['common.erPrsiableGross'] ??
                        'Employer PRSIable Gross'
                      }
                      name="erPrsiGross"
                    >
                      <AppCurrencyInput
                        prefix={showCurrencyByCountry(payrollCountry)}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.erPrsi'] ?? 'Employer PRSI'}
                      name="erPRSI"
                    >
                      <AppCurrencyInput
                        prefix={showCurrencyByCountry(payrollCountry)}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.uscableGross'] ?? 'USCable Gross'}
                      name="uscGross"
                    >
                      <AppCurrencyInput
                        prefix={showCurrencyByCountry(payrollCountry)}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.usc'] ?? 'USC'}
                      name="usc"
                    >
                      <AppCurrencyInput
                        prefix={showCurrencyByCountry(payrollCountry)}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.lpt'] ?? 'LPT'}
                      name="lptDeduction"
                    >
                      <AppCurrencyInput
                        prefix={showCurrencyByCountry(payrollCountry)}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={
                        messages['common.pensionableGross'] ??
                        'Pensionable Gross'
                      }
                      name="pensionGross"
                    >
                      <AppCurrencyInput
                        prefix={showCurrencyByCountry(payrollCountry)}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.employeePension']}
                      name="employeePension"
                    >
                      <AppCurrencyInput
                        prefix={showCurrencyByCountry(payrollCountry)}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.employerPension']}
                      name="employerPension"
                    >
                      <AppCurrencyInput
                        prefix={showCurrencyByCountry(payrollCountry)}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                {/* Statutories Year to Date */}
                <div className="h-[4px] w-full bg-neutral-100 rounded-full mb-6" />
                <div className="text-[16px] mb-4">
                  {messages['common.statutoryYearToDate'] ??
                    'Statutories Year to Date'}
                </div>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={messages['common.ssp'] ?? 'SSP'}
                      name="ssp"
                    >
                      <AppCurrencyInput
                        prefix={showCurrencyByCountry(payrollCountry)}
                        placeholder={messages['form.enter']}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </>
        )}
      </AppFormContainer>

      {/* ── CURRENT EMPLOYMENT YTD (Collapsible) - hidden for CIS/Non-CIS ── */}
      {!isCisNonCis && (
        <Collapse
          className="mt-4"
          defaultActiveKey={[]}
          items={[
            {
              key: 'ytd',
              label: (
                <Typography.Text className="!text-[16px] !font-semibold">
                  {messages['common.currentEmploymentYTD']}
                </Typography.Text>
              ),
              children: (
                <div>
                  {/* ── Shared fields: Tax, Gross For Tax ── */}
                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <div className="mb-4">
                        <div className="text-[14px] text-[#6F767E] mb-1">
                          {messages['common.tax']}
                        </div>
                        <AppInput
                          disabled
                          value={formatMoney(
                            payrollSettingData?.ytdValues?.tax || 0,
                            '',
                          )}
                        />
                      </div>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div className="mb-4">
                        <div className="text-[14px] text-[#6F767E] mb-1">
                          {messages['common.grossForTax']}
                        </div>
                        <AppInput
                          disabled
                          value={formatMoney(
                            payrollSettingData?.ytdValues?.grossForTax || 0,
                            '',
                          )}
                        />
                      </div>
                    </Col>
                  </Row>

                  {/* ── UK-only fields: NI, Student Loan, PG Loan ── */}
                  {!isIreland && (
                    <>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <div className="mb-4">
                            <div className="text-[14px] text-[#6F767E] mb-1">
                              {messages['common.employeeNI']}
                            </div>
                            <AppInput
                              disabled
                              value={formatMoney(
                                payrollSettingData?.ytdValues?.eeNI || 0,
                                '',
                              )}
                            />
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div className="mb-4">
                            <div className="text-[14px] text-[#6F767E] mb-1">
                              {messages['common.grossForNI']}
                            </div>
                            <AppInput
                              disabled
                              value={formatMoney(
                                payrollSettingData?.ytdValues?.grossForNI || 0,
                                '',
                              )}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <div className="mb-4">
                            <div className="text-[14px] text-[#6F767E] mb-1">
                              {messages['common.employerNI']}
                            </div>
                            <AppInput
                              disabled
                              value={formatMoney(
                                payrollSettingData?.ytdValues?.erNI || 0,
                                '',
                              )}
                            />
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div className="mb-4">
                            <div className="text-[14px] text-[#6F767E] mb-1">
                              {messages['common.studentLoan']}
                            </div>
                            <AppInput
                              disabled
                              value={formatMoney(
                                payrollSettingData?.ytdValues?.stLoan || 0,
                                '',
                              )}
                            />
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}

                  {/* ── Ireland-only fields: PRSI, USC, LPT ── */}
                  {isIreland && (
                    <>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <div className="mb-4">
                            <div className="text-[14px] text-[#6F767E] mb-1">
                              {messages['common.employeePrsiAbleGross']}
                            </div>
                            <AppInput
                              disabled
                              value={formatMoney(
                                payrollSettingData?.ytdValues?.eePrsiGross || 0,
                                '',
                              )}
                            />
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div className="mb-4">
                            <div className="text-[14px] text-[#6F767E] mb-1">
                              {messages['common.employeePrsi']}
                            </div>
                            <AppInput
                              disabled
                              value={formatMoney(
                                payrollSettingData?.ytdValues?.eePRSI || 0,
                                '',
                              )}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <div className="mb-4">
                            <div className="text-[14px] text-[#6F767E] mb-1">
                              {messages['common.employerPrsiAbleGross']}
                            </div>
                            <AppInput
                              disabled
                              value={formatMoney(
                                payrollSettingData?.ytdValues?.erPrsiGross || 0,
                                '',
                              )}
                            />
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div className="mb-4">
                            <div className="text-[14px] text-[#6F767E] mb-1">
                              {messages['common.employerPrsi']}
                            </div>
                            <AppInput
                              disabled
                              value={formatMoney(
                                payrollSettingData?.ytdValues?.erPRSI || 0,
                                '',
                              )}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <div className="mb-4">
                            <div className="text-[14px] text-[#6F767E] mb-1">
                              {messages['common.uscAbleGross']}
                            </div>
                            <AppInput
                              disabled
                              value={formatMoney(
                                payrollSettingData?.ytdValues?.uscGross || 0,
                                '',
                              )}
                            />
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div className="mb-4">
                            <div className="text-[14px] text-[#6F767E] mb-1">
                              {messages['common.usc']}
                            </div>
                            <AppInput
                              disabled
                              value={formatMoney(
                                payrollSettingData?.ytdValues?.usc || 0,
                                '',
                              )}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          <div className="mb-4">
                            <div className="text-[14px] text-[#6F767E] mb-1">
                              {messages['common.lpt']}
                            </div>
                            <AppInput
                              disabled
                              value={formatMoney(
                                payrollSettingData?.ytdValues?.lptDeduction ||
                                  0,
                                '',
                              )}
                            />
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div className="mb-4">
                            <div className="text-[14px] text-[#6F767E] mb-1">
                              {messages['common.pensionableGross']}
                            </div>
                            <AppInput
                              disabled
                              value={formatMoney(
                                payrollSettingData?.ytdValues?.pensionGross ||
                                  0,
                                '',
                              )}
                            />
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}

                  {/* ── Shared fields: Pension ── */}
                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <div className="mb-4">
                        <div className="text-[14px] text-[#6F767E] mb-1">
                          {messages['common.employeePension']}
                        </div>
                        <AppInput
                          disabled
                          value={formatMoney(
                            payrollSettingData?.ytdValues?.eePension || 0,
                            '',
                          )}
                        />
                      </div>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div className="mb-4">
                        <div className="text-[14px] text-[#6F767E] mb-1">
                          {messages['common.employerPension']}
                        </div>
                        <AppInput
                          disabled
                          value={formatMoney(
                            payrollSettingData?.ytdValues?.erPension || 0,
                            '',
                          )}
                        />
                      </div>
                    </Col>
                  </Row>

                  {/* ── UK-only: PG Student Loan ── */}
                  {!isIreland && (
                    <div className="mb-4">
                      <div className="text-[14px] text-[#6F767E] mb-1">
                        {messages['common.postGraduateStudentLoan']}
                      </div>
                      <AppInput
                        disabled
                        value={formatMoney(
                          payrollSettingData?.ytdValues?.pgLoan || 0,
                          '',
                        )}
                      />
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* ── ACTIONS ── */}
      {!readOnly && !inDrawer && (
        <div className="flex items-center justify-start gap-3 pt-6">
          <AppButton
            type="primary"
            name={messages['common.save']}
            leftIcon={
              <Icons.IconCheckCircle color={defaultTheme.theme.palette.white} />
            }
            onClick={handleSave}
            loading={isUpdatingSettingEmployee}
          />
        </div>
      )}
    </div>
  );
};

export default SettingsGeneral;
